import { useEffect, useState, type FormEvent } from "react";

import { Button } from "../../components/Button/Button";
import { Dialog } from "../../components/Dialog/Dialog";
import { FormField, TextInput } from "../../components/FormField/FormField";
import { ApiError, type UserRow, api } from "../../lib/api";
import { fullName } from "../../lib/users";

interface PasswordDialogProps {
  open: boolean;
  user: UserRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export function PasswordDialog({ open, user, onClose, onSaved }: PasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirm("");
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("A palavra-passe tem de ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("As palavras-passe não coincidem.");
      return;
    }
    if (!user) return;

    setSubmitting(true);
    try {
      await api.updateUser(user.id, { password });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível alterar a palavra-passe.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={user ? `Mudar palavra-passe — ${fullName(user)}` : "Mudar palavra-passe"}
      size="sm"
      footer={
        <>
          <Button variant="ghost" size="md" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "A actualizar…" : "Actualizar"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <FormField label="Nova palavra-passe" htmlFor="new-password" hint="Mínimo de 8 caracteres.">
          <TextInput
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
          />
        </FormField>
        <FormField label="Confirmar palavra-passe" htmlFor="confirm-password">
          <TextInput
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
          />
        </FormField>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "#ffe5e5",
              color: "#b00020",
              borderRadius: 12,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
      </form>
    </Dialog>
  );
}
